import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessagesSquare, Mic, UserRound, ShieldCheck, ArrowRight, Sparkles, BadgeCheck, Lock, Flame, Coffee } from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import VoiceOrb from '../components/VoiceOrb';
import PersonaAvatar from '../components/PersonaAvatar';
import { Squiggle, Sparkle, ScribbleArrow, DottedPath } from '../components/Doodles';
import { ScoreRing } from '../components/ui';
import useMeta from '../components/useMeta';
import BRAND from '../config/brand';
import illuChat from '../assets/img/illu-chat.png';
import illuHero from '../assets/img/illu-hero.png';
import illuDate from '../assets/img/illu-date.png';

const BUBBLES = [
  { text: '"arre that opener actually worked 😄"', x: '-8%', y: '4%', delay: 0 },
  { text: 'confidence: 5 → 8 in two weeks 📈', x: '58%', y: '-2%', delay: 1.2, hideMobile: true },
  { text: '"haan, coffee on friday?" — she said yes ☕', x: '-4%', y: '74%', delay: 0.6, hideMobile: true },
  { text: 'streak: 12 days 🔥', x: '66%', y: '80%', delay: 1.8, hideMobile: true },
];

const PERSONAS = [
  { key: 'friendly', name: 'aisha', vibe: 'warm & easy' },
  { key: 'neutral', name: 'priya', vibe: 'you drive it' },
  { key: 'reserved', name: 'meera', vibe: 'earn her words' },
  { key: 'hard', name: 'tara', vibe: 'the final boss' },
];

const FAQS = [
  { q: 'is this a dating app?', a: 'No. It\u2019s your private practice space — no matching, no feed, nobody ever sees your stuff. Practice here, shine on the apps you already use.' },
  { q: 'what does the ₹99 trial include?', a: '7 days of everything: 5 chat rescues, 1 full profile review, 1 voice practice date. Auto-converts via UPI autopay — cancel before day 7 and pay nothing more.' },
  { q: 'hinglish chalega?', a: 'Bilkul! Your coach mirrors your mix — pure English, pure Hindi vibes, or full Hinglish. Every practice partner is built with Indian context.' },
  { q: 'will it teach me pickup lines?', a: 'Never. Everything is built on honesty, respect, and genuine connection. When someone\u2019s not interested, you learn the most attractive skill of all — graceful acceptance.' },
  { q: 'is my data private?', a: 'Completely. Single-player by design. Delete your account anytime and everything is hard-deleted, forever.' },
];

function Bubble({ text, x, y, delay, hideMobile }) {
  return (
    <motion.div
      className={`absolute glass rounded-2xl px-4 py-2.5 text-sm font-semibold text-sub whitespace-nowrap ${hideMobile ? 'max-sm:hidden' : ''}`}
      style={{ left: x, top: y }}
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay }}
    >
      {text}
    </motion.div>
  );
}

function IlluFrame({ src, alt, className = '', testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`relative rounded-[2rem] overflow-hidden border border-line shadow-soft bg-paper ${className}`}
      data-testid={testId}
    >
      <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
    </motion.div>
  );
}

export default function Landing() {
  useMeta({ title: null, description: BRAND.description });

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-ember-light/50 blur-3xl pointer-events-none" />
        <div className="absolute top-64 -left-40 w-[420px] h-[420px] rounded-full bg-butter/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-14 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <Sparkle className="absolute -top-6 right-10 text-sun hidden sm:block" size={22} delay={0.8} />
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-xs font-bold text-sub mb-6" data-testid="hero-badge">
              <Lock size={13} className="text-ember" /> 100% private · made for India · 18+
            </div>
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.0] lowercase" data-testid="hero-headline">
              practice before<br />it <span className="text-ember relative inline-block">matters.
                <Squiggle className="absolute -bottom-3 left-0 text-sun w-full" />
              </span>
            </h1>
            <p className="text-sub text-base sm:text-lg mt-6 max-w-md leading-relaxed">
              Your private coach for the whole game — chats, calls, profile.
            </p>
            <div className="mt-6 space-y-2.5 max-w-md" data-testid="hero-feature-glimpse">
              {[
                [Mic, 'voice mock dates', 'real-time calls with 4 lifelike practice partners', 'bg-butter/60 text-sun'],
                [MessagesSquare, 'chat coach', 'paste a convo or upload a screenshot → 3 ready replies', 'bg-ember-light/60 text-ember'],
                [UserRound, 'profile optimizer', 'honest score, bio rewrites, photo verdicts', 'bg-sage/40 text-mint'],
              ].map(([Icon, t, s, c]) => (
                <div key={t} className="flex items-center gap-3.5 card !rounded-2xl px-4 py-3" data-testid={`glimpse-${t.split(' ')[0]}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${c}`}><Icon size={17} /></div>
                  <div className="min-w-0">
                    <span className="font-heading font-black text-sm lowercase">{t}</span>
                    <span className="text-faint text-xs block sm:inline sm:ml-2">{s}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Link to="/auth?mode=signup" className="btn-primary text-base !min-h-[54px] !px-8" data-testid="hero-cta-button">
                start the {BRAND.trialPrice} trial <ArrowRight size={18} />
              </Link>
              <a href="#partners" className="btn-secondary !min-h-[54px]" data-testid="hero-how-link">meet your partners</a>
            </div>
            <p className="text-faint text-xs mt-4">7 days · then {BRAND.monthlyPrice}/month · cancel in two taps</p>
          </div>

          {/* Orb + floating moments */}
          <div className="relative h-[360px] sm:h-[420px] flex items-center justify-center" data-testid="hero-visual">
            <Sparkle className="absolute top-6 left-[14%] text-ember" size={18} delay={0.3} />
            <Sparkle className="absolute bottom-16 right-[10%] text-sun" size={24} delay={1.4} />
            <VoiceOrb size={250} active />
            {BUBBLES.map((b, i) => <Bubble key={i} {...b} />)}
            <motion.div
              className="absolute bottom-[12%] left-1/2 -translate-x-1/2 glass rounded-full px-5 py-2.5 text-sm font-bold text-ink flex items-center gap-2"
              animate={{ y: [0, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
            >
              <Mic size={14} className="text-ember" /> "so... tell me something not on your profile?"
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE HARD TRUTH — hinglish statement */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-3">the hard truth</div>
            <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight lowercase leading-[1.05]" data-testid="truth-headline">
              problem tum mein nahi,<br /><span className="text-ember">tumhare texts</span> mein hai.
            </h2>
            <p className="text-sub text-base sm:text-lg mt-5 max-w-md leading-relaxed">
              Three weeks of "haha nice" never turned into a coffee date. Paste the dying chat — get 3 replies that sound like you, on your best day.
            </p>
            <div className="mt-7 space-y-3 max-w-md">
              <div className="flex items-start gap-3">
                <span className="bg-elevated border border-line rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-faint line-through decoration-ember/60">hmm thik hai 👍</span>
                <span className="text-faint text-xs font-bold mt-2.5 shrink-0">before</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-ember text-white rounded-2xl rounded-bl-md px-4 py-2.5 text-sm shadow-glow-sm">wait — you trek AND roast my playlist? coffee. friday. non-negotiable ☕</span>
                <span className="text-ember text-xs font-bold mt-2.5 shrink-0">after</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <ScribbleArrow className="absolute -top-10 -left-4 text-ember hidden lg:block" />
            <IlluFrame src={illuChat} alt="Chat rescue illustration" className="max-w-md mx-auto" testId="truth-illustration" />
            <motion.div
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 glass rounded-full px-4 py-2 text-xs font-bold text-ink flex items-center gap-1.5"
              animate={{ y: [0, -6, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Sparkles size={12} className="text-ember" /> left on read? not anymore.
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRACTICE PARTNERS */}
      <section id="partners" className="max-w-6xl mx-auto px-6 md:px-12 py-16 sm:py-24">
        <div className="overline mb-3">your practice partners</div>
        <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight lowercase">four personalities.<br />one goal — the confident you.</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-12">
          {PERSONAS.map((p, i) => (
            <motion.div key={p.key} whileHover={{ y: -6 }} className="card p-6 text-center" data-testid={`landing-persona-${p.key}`}>
              <div className="flex justify-center"><PersonaAvatar persona={p.key} size={84} /></div>
              <div className="font-heading font-black text-xl mt-4 lowercase">{p.name}</div>
              <div className="text-faint text-sm">{p.vibe}</div>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-sub text-sm mt-8">Real-time voice dates with live captions — then an honest scorecard. Even Tara is winnable. 😉</p>
      </section>

      {/* FEATURE TRIO — visual mocks, minimal text */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-10 sm:py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Chat coach */}
          <motion.div whileHover={{ y: -6 }} className="card p-7" data-testid="feature-chat-coach">
            <div className="w-12 h-12 rounded-2xl bg-ember-light/60 flex items-center justify-center mb-5"><MessagesSquare size={22} className="text-ember" /></div>
            <h3 className="font-heading font-black text-xl lowercase">chat rescue</h3>
            <p className="text-sub text-sm mt-2">Stuck convo in → 3 ready replies out.</p>
            <div className="mt-5 space-y-2">
              <div className="bg-elevated rounded-2xl rounded-bl-md px-4 py-2.5 text-sm w-fit">spiti is my dream trip yaar 🏔️</div>
              <div className="bg-ember text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm w-fit ml-auto">come judge my trek pics over coffee?</div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-mint"><Sparkles size={12} /> bold · turns her interest into a plan</div>
            </div>
          </motion.div>

          {/* Voice */}
          <motion.div whileHover={{ y: -6 }} className="card p-7" data-testid="feature-voice">
            <div className="w-12 h-12 rounded-2xl bg-butter/60 flex items-center justify-center mb-5"><Mic size={22} className="text-sun" /></div>
            <h3 className="font-heading font-black text-xl lowercase">voice practice dates</h3>
            <p className="text-sub text-sm mt-2">Your "hello" shouldn't sound like a job interview.</p>
            <div className="mt-5 flex flex-col items-center">
              <VoiceOrb size={110} />
              <div className="glass rounded-full px-4 py-2 text-xs font-semibold text-sub -mt-3">"accha, that's actually interesting..."</div>
              <div className="flex gap-2 mt-3">
                {['confidence 8', 'humor 7', 'flow 8'].map((s) => (
                  <span key={s} className="bg-sage/30 text-mint text-[11px] font-bold rounded-full px-2.5 py-1">{s}</span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Profile */}
          <motion.div whileHover={{ y: -6 }} className="card p-7" data-testid="feature-profile">
            <div className="w-12 h-12 rounded-2xl bg-sage/40 flex items-center justify-center mb-5"><UserRound size={22} className="text-mint" /></div>
            <h3 className="font-heading font-black text-xl lowercase">profile glow-up</h3>
            <p className="text-sub text-sm mt-2">"I love travelling" is not a personality. Get the honest fix.</p>
            <div className="mt-5 flex items-center gap-4">
              <ScoreRing score={78} size={84} label="of 100" />
              <div className="space-y-2 text-xs font-semibold text-sub">
                <div className="flex items-center gap-1.5"><BadgeCheck size={13} className="text-mint" /> verified real photo</div>
                <div className="flex items-center gap-1.5"><Sparkles size={13} className="text-ember" /> 3 bio rewrites</div>
                <div className="flex items-center gap-1.5"><Flame size={13} className="text-sun" /> top 3 fixes</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS — illustration + steps */}
      <section id="how" className="max-w-6xl mx-auto px-6 md:px-12 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <IlluFrame src={illuHero} alt="Daily practice illustration" className="max-w-md mx-auto order-2 lg:order-1" testId="how-illustration" />
          <div className="order-1 lg:order-2">
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight lowercase">ten minutes a day.<br />that's it.</h2>
            <p className="text-sub text-base mt-3">Gym for your social game. Minus the protein shakes.</p>
            <DottedPath className="text-ember/50 w-full h-8 mt-6 hidden sm:block" />
            <div className="space-y-4 mt-6">
              {[['1', 'tell us where you are', '60-second quiz. no judgement.'], ['2', 'practice in private', 'chats, calls, profile — nobody watching'], ['3', 'show up confident', 'streaks, scorecards, actual dates']].map(([n, t, s]) => (
                <div key={n} className="card p-5 flex items-center gap-4" data-testid={`how-step-${n}`}>
                  <div className="w-10 h-10 rounded-full bg-ember text-white font-heading font-black flex items-center justify-center shrink-0">{n}</div>
                  <div>
                    <div className="font-heading font-bold lowercase">{t}</div>
                    <div className="text-faint text-sm">{s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SAFETY STRIP */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-8">
        <div className="card !bg-sage/15 !border-sage/40 p-8 sm:p-10 flex flex-col sm:flex-row gap-5 items-start sm:items-center">
          <div className="w-12 h-12 rounded-2xl bg-mint/15 flex items-center justify-center shrink-0"><ShieldCheck size={22} className="text-mint" /></div>
          <div>
            <h3 className="font-heading font-black text-xl lowercase">zero sleaze, by design</h3>
            <p className="text-sub text-sm mt-1.5 max-w-2xl">Honesty, respect, consent — always. No manipulation tricks, no negging, no chasing after a no.</p>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 sm:py-24" id="pricing">
        <div className="overline mb-3 text-center">pricing</div>
        <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight lowercase text-center">less than one coffee date<br />a month.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12 items-stretch">
          <div className="card p-7 flex flex-col" data-testid="pricing-card-trial">
            <div className="font-heading font-bold lowercase text-faint">try it</div>
            <div className="mt-2"><span className="font-heading font-black text-4xl">{BRAND.trialPrice}</span><span className="text-faint text-sm"> / 7 days</span></div>
            <ul className="mt-5 space-y-2 text-sm text-sub flex-1">
              <li>5 chat rescues</li><li>1 profile review</li><li>1 voice practice date</li>
            </ul>
            <Link to="/auth?mode=signup" className="btn-secondary w-full mt-6" data-testid="pricing-trial-cta">start trial</Link>
          </div>
          <div className="card p-7 !border-ember !border-2 relative flex flex-col shadow-lift" data-testid="pricing-card-monthly">
            <div className="absolute -top-3 left-6"><span className="bg-ember text-white text-xs font-bold px-3 py-1 rounded-full">most popular</span></div>
            <div className="font-heading font-bold lowercase text-ember">monthly</div>
            <div className="mt-2"><span className="font-heading font-black text-4xl">{BRAND.monthlyPrice}</span><span className="text-faint text-sm"> / month</span></div>
            <ul className="mt-5 space-y-2 text-sm text-sub flex-1">
              <li>Unlimited chat rescues</li><li>30 voice dates / month</li><li>4 profile reviews / month</li><li>All four partners</li>
            </ul>
            <Link to="/auth?mode=signup" className="btn-primary w-full mt-6" data-testid="pricing-monthly-cta">start with {BRAND.trialPrice} trial</Link>
          </div>
          <div className="card p-7 flex flex-col" data-testid="pricing-card-quarterly">
            <div className="font-heading font-bold lowercase text-faint">quarterly</div>
            <div className="mt-2"><span className="font-heading font-black text-4xl">{BRAND.quarterlyPrice}</span><span className="text-faint text-sm"> / 3 months</span></div>
            <ul className="mt-5 space-y-2 text-sm text-sub flex-1">
              <li>Everything in monthly</li><li>Save 17%</li><li>Full glow-up arc</li>
            </ul>
            <Link to="/auth?mode=signup" className="btn-secondary w-full mt-6" data-testid="pricing-quarterly-cta">start with {BRAND.trialPrice} trial</Link>
          </div>
        </div>
      </section>

      {/* GRADUATION — the real goal */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <Sparkle className="absolute -top-4 right-8 text-sun hidden lg:block" size={20} delay={0.6} />
            <IlluFrame src={illuDate} alt="Real coffee date illustration" className="max-w-md mx-auto" testId="graduation-illustration" />
          </div>
          <div>
            <div className="overline mb-3">the actual goal</div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight lowercase leading-[1.1]" data-testid="graduation-headline">
              the goal isn't the app.<br />it's <span className="text-ember">the coffee.</span> ☕
            </h2>
            <p className="text-sub text-base mt-4 max-w-md leading-relaxed">
              Practice here so you can graduate — to real dates, real banter, the real you. When you stop needing us, we call that a win.
            </p>
            <div className="flex items-center gap-2 mt-6 text-sm font-bold text-mint">
              <Coffee size={16} /> best case scenario: you cancel because you're busy on saturdays.
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-16">
        <h2 className="font-heading font-black text-3xl tracking-tight lowercase">fair questions</h2>
        <div className="mt-8 space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="card p-5 group" data-testid={`faq-item-${i}`}>
              <summary className="font-heading font-bold cursor-pointer list-none flex items-center justify-between gap-4 lowercase">
                {f.q}
                <span className="text-ember text-xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-sub text-sm mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-10">
        <div className="card !bg-gradient-to-br !from-ember !to-sun !border-0 p-10 sm:p-14 text-center text-white relative overflow-hidden">
          <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight lowercase relative">your next date deserves<br />the prepared you.</h2>
          <Link to="/auth?mode=signup" className="btn-primary !bg-white !text-ember hover:!bg-bg text-base !min-h-[54px] !px-8 mt-8 inline-flex relative" data-testid="final-cta-button">
            start the {BRAND.trialPrice} trial <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Persistent mobile CTA */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 p-3 pb-[max(12px,env(safe-area-inset-bottom))] bg-gradient-to-t from-bg via-bg/95 to-transparent">
        <Link to="/auth?mode=signup" className="btn-primary w-full" data-testid="sticky-mobile-cta">start the {BRAND.trialPrice} trial</Link>
      </div>
    </PublicLayout>
  );
}
